<div class="reports-container">
    <div class="header-actions">
        <div class="header-left">
            <h2 class="section-title">Analysis & Review (Gemini 3.5)</h2>
            <div class="stats-bar" *ngIf="stats">
                <div class="stat-item">
                    <span class="label">Total:</span>
                    <span class="value">{{ stats.total_count | number }}</span>
                </div>
                <div class="stat-item mismatch">
                    <span class="label">Mismatch:</span>
                    <span class="value">{{ stats.mismatch_count | number }}</span>
                </div>
                <div class="stat-item match-rate">
                    <span class="label">Match Rate:</span>
                    <span class="value">{{ stats.match_rate | number:'1.1-2' }}%</span>
                </div>
            </div>
        </div>

        <div class="actions-right">
            <button mat-stroked-button color="primary" (click)="openImportDialog()" [disabled]="showProgressContainer">
                <mat-icon>upload_file</mat-icon> Import Excel
            </button>
            <button mat-stroked-button color="primary" (click)="onExport()" [disabled]="loading || showProgressContainer">
                <mat-icon>download</mat-icon> Export CSV
            </button>
            <button mat-stroked-button color="primary" (click)="openReviewDialog()" [disabled]="showProgressContainer">
                <mat-icon>upload_file</mat-icon> Upload Review (Excel)
            </button>
            
            <div class="search-box">
                <mat-form-field appearance="outline" class="search-field">
                    <mat-label>Search</mat-label>
                    <input matInput [(ngModel)]="searchQuery" (keyup.enter)="onSearch()">
                    <button mat-icon-button matSuffix (click)="onSearch()">
                        <mat-icon>search</mat-icon>
                    </button>
                </mat-form-field>
            </div>
        </div>
    </div>

    <div class="progress-container" *ngIf="showProgressContainer" [ngClass]="{'is-error': uploadStatus === 'error'}">
        <div class="progress-header">
            <span class="status-text">
                <mat-icon *ngIf="uploadStatus === 'error'">error_outline</mat-icon>
                {{ uploadStatusText }}
            </span>
            <span *ngIf="uploadStatus !== 'error'">{{ uploadProgress | number:'1.0-0' }}%</span>
        </div>
        <mat-progress-bar 
            [mode]="uploadStatus === 'error' ? 'determinate' : 'determinate'"
            [color]="uploadStatus === 'error' ? 'warn' : 'primary'"
            [value]="uploadProgress">
        </mat-progress-bar>
        <div class="progress-actions" *ngIf="uploadStatus === 'error'">
            <button mat-stroked-button color="warn" (click)="closeProgress()">Close</button>
        </div>
    </div>

    <div class="table-container" #tableContainer>
        <div class="loading-shade" *ngIf="loading">
            <mat-spinner diameter="40"></mat-spinner>
        </div>

        <table mat-table [dataSource]="dataSource">

            <ng-container matColumnDef="date">
                <th mat-header-cell *matHeaderCellDef style="width: 130px; cursor: pointer; user-select: none;" (click)="toggleDateSort()">
                    <div style="display: flex; align-items: center; gap: 4px;">
                        Date
                        <mat-icon style="font-size: 16px; width: 16px; height: 16px;">
                            {{ dateSortDirection === 'desc' ? 'arrow_downward' : 'arrow_upward' }}
                        </mat-icon>
                    </div>
                </th>
                <td mat-cell *matCellDef="let row" class="date-cell"> {{row.acquisition_date}} </td>
            </ng-container>

            <ng-container matColumnDef="company">
                <th mat-header-cell *matHeaderCellDef style="width: 140px;"> Company </th>
                <td mat-cell *matCellDef="let row">
                    <div class="company-name">{{row.company_name}}</div>
                    <div class="lbc-code">{{row.lbc_code}}</div>
                </td>
            </ng-container>

            <ng-container matColumnDef="detail">
                <th mat-header-cell *matHeaderCellDef> Detail </th>
                <td mat-cell *matCellDef="let row" class="detail-cell">
                    <div *ngIf="row.source_medium_category && !row.source_medium_category.startsWith(' ')"
                        class="source-tag">
                        {{row.source_medium_category}}
                    </div>
                    
                    <div class="detail-text" [matTooltip]="row.source_detailed_category"
                        matTooltipClass="reason-tooltip" matTooltipPosition="above">
                        {{row.source_detailed_category}}
                    </div>

                    <div *ngIf="row.data_name" class="data-name-tag">
                        {{row.data_name}}
                    </div>
                    
                    <div *ngIf="row.original_label" class="original-label-tag" [class.mismatch]="row.isMismatch">
                        <strong>{{row.original_label}}</strong>
                    </div>
                </td>
            </ng-container>

            <ng-container matColumnDef="manual_check">
                <th mat-header-cell *matHeaderCellDef style="width: 160px;"> Truth (Label) </th>
                <td mat-cell *matCellDef="let row">
                    <br>
                    <mat-form-field appearance="outline" class="dense-select">
                        <mat-select [(ngModel)]="row.editingFlag" (selectionChange)="onAutoSave(row)"
                            placeholder="Select">
                            <mat-option *ngFor="let opt of flagOptions" [value]="opt.value">
                                {{opt.label}}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                    
                    <div class="version-list-in-cell">
                        <div class="v-list-title">AI Suggestions:</div>
                        <div *ngFor="let v of row.versionList" class="version-item"
                            [class.match]="v.exists && !v.isMismatch" [class.mismatch]="v.exists && v.isMismatch"
                            [class.empty]="!v.exists">
                            <span class="v-label">V{{v.version}}</span>
                            <span class="v-val" [matTooltip]="v.reason" matTooltipClass="reason-tooltip">
                                {{v.pred}}
                            </span>
                        </div>
                    </div>

                </td>
            </ng-container>

            <ng-container matColumnDef="reason">
                <th mat-header-cell *matHeaderCellDef style="width: 25%;"> Review Memo </th>
                <td mat-cell *matCellDef="let row">
                    <mat-form-field appearance="outline" class="dense-input" style="width: 100%;">
                        <input matInput [(ngModel)]="row.editingReason" (blur)="onAutoSave(row)"
                            (keyup.enter)="onAutoSave(row)" placeholder="Reason...">
                    </mat-form-field>
                </td>
            </ng-container>

            <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef style="width: 80px; text-align: center;"> Action </th>
                <td mat-cell *matCellDef="let row" style="text-align: center;">
                    <button 
                        mat-icon-button 
                        color="warn" 
                        matTooltip="Delete" 
                        (click)="deleteItem(row)" 
                        [disabled]="row.isDeletingMap || row.isSaving">
                        <mat-icon>delete</mat-icon>
                    </button>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.deleting-row]="isDeletingMap[row.id]"></tr>
        </table>

        <div class="paginator">
            <button mat-button (click)="onPrevPage()" [disabled]="skip === 0 || loading">
                <div class="paging-btn-content">
                    <mat-spinner *ngIf="pagingState === 'prev'" diameter="16"></mat-spinner>
                    <span>Previous</span>
                </div>
            </button>

            <span>{{ dataSource.length > 0 ? (skip + 1) : 0 }} 〜 {{ skip + dataSource.length }}</span>

            <button mat-button (click)="onNextPage()" [disabled]="dataSource.length < limit || loading">
                <div class="paging-btn-content">
                    <span>Next</span>
                    <mat-spinner *ngIf="pagingState === 'next'" diameter="16"></mat-spinner>
                </div>
            </button>
        </div>
    </div>
</div>

<ng-template #importDialog>
    <h2 mat-dialog-title>Import Excel Data</h2>
    <mat-dialog-content>
        <div class="import-dialog-content">
            <p class="dialog-desc">Select an Excel file and enter the data name.</p>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Data Name (Attribute)</mat-label>
                <input matInput [(ngModel)]="importDataName" placeholder="e.g. TestData" required>
            </mat-form-field>

            <div class="file-selection-area">
                <button mat-stroked-button (click)="fileInputInDialog.click()">
                    <mat-icon>folder_open</mat-icon> Choose File
                </button>
                <input #fileInputInDialog type="file" style="display: none" (change)="onImportFileSelected($event)"
                    accept=".xlsx">
                <span class="file-name" [class.placeholder]="!importFileName">
                    {{ importFileName || 'No file selected' }}
                </span>
            </div>
        </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-flat-button color="primary" [disabled]="!selectedImportFile" (click)="onUpload()">
            Upload
        </button>
    </mat-dialog-actions>
</ng-template>

<ng-template #reviewDialog>
    <h2 mat-dialog-title>Upload Review Excel Data</h2>
    <mat-dialog-content>
        <div class="import-dialog-content">
            <p class="dialog-desc">Select a review Excel file to bulk-update confirmed labels.</p>
            
            <div class="file-selection-area">
                <button mat-stroked-button (click)="reviewFileInputInDialog.click()">
                    <mat-icon>folder_open</mat-icon> Choose File
                </button>
                <input #reviewFileInputInDialog type="file" style="display: none" (change)="onReviewFileSelected($event)"
                    accept=".xlsx">
                <span class="file-name" [class.placeholder]="!reviewFileName">
                    {{ reviewFileName || 'No file selected' }}
                </span>
            </div>
        </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close>Cancel</button>
        <button mat-flat-button color="primary" [disabled]="!selectedReviewFile" (click)="onUploadReviewExcel()">
            Upload
        </button>
    </mat-dialog-actions>
</ng-template>